import { useQueryClient } from 'react-query'
import {
  Order,
  OrdersApi,
  Refund,
  RefundCreateParams,
  SuccessResponse,
} from '@citruscamps/citrus-client'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

interface IProps {
  programId?: string
}

interface IUpdateOrders {
  handleCancel: (order: Order) => Promise<void>
  handleDispute: (order: Order) => any
  handleRefund: (order: Order, refund: RefundCreateParams) => Promise<void>
  handleSendEmail: (order: Order) => any
}

/**
 * @deprecated TODO: Product financial foundation is not implemented yet
 * ⚠️ This will be removed in the future when we migrate to products
 **/
export const useUpdateOrders = ({ programId }: IProps): IUpdateOrders => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()

  const handleCancel = async (order: Order) => {
    const orderClient = new OrdersApi(generateApiConfig())
    if (programId) {
      try {
        await requestHandler<Order>(
          () => orderClient.cancelOrder({ program_id: programId, order_id: order.id }),
          {
            toast: {
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'order',
            program_id: programId,
          }),
        )
      } catch {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleDispute = async () => {
    window.open('https://dashboard.stripe.com/disputes', '_blank')
  }

  const handleRefund = async (order: Order, refund: RefundCreateParams) => {
    const refundClient = new OrdersApi(generateApiConfig())
    if (programId) {
      try {
        await requestHandler<Refund[]>(
          () =>
            refundClient.refundRefund({
              program_id: programId,
              order_id: order.id,
              RefundCreateParams: refund,
            }),
          {
            toast: {
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'order',
            program_id: programId,
          }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
        throw e
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleSendEmail = async (order: Order) => {
    const client = new OrdersApi(generateApiConfig())
    if (programId) {
      try {
        await requestHandler<SuccessResponse>(
          () => client.resendOrder({ program_id: programId, order_id: order.id }),
          {
            toast: {
              setToast,
            },
          },
        )
        setToast?.({
          header: (translations as any)['success.default_toast_header'],
          message: (translations as any)['success.send_order_message'],
          type: 'success',
        })
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
        throw e
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  return {
    handleCancel,
    handleDispute,
    handleRefund,
    handleSendEmail,
  }
}
